import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { ColumnIndexes, ColumnKeys, DevicesAppWebComponentSchema } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { translateFn } from '../../../utils/translator.helper';
import { convertGenericViewConfigToGridViewConfig } from '../../../grid/genericGridSmartViews/types';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from '../../../grid/genericGridSmartViews/utils';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { TemplateState } from '../../../types/common/types';
import {
  changeEmptyStateIcon,
  getCompanyFilterUiConfig,
  getOSTypeColumnValues,
  getStateColumnValues,
  getStatusColumnValues,
  getWatchlistColumnValues,
  processExternalFilters,
  setBulkActions,
  setGridCommonProperties,
  setTranslatedStrings,
} from '../../common/utils';
import {
  companyRenderer,
  fqdnRenderer,
  ipRenderer,
  osRenderer,
  resourceIdRenderer,
  riskScoreRenderer,
} from '../../common/customRenderers';
import { DevicesAppGridDataProvider } from '../dataProvider/DevicesAppGridDataProvider';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import {
  actionsColumnRenderer,
  deviceMacsRenderer,
  deviceMisconfigurationsRenderer,
  deviceTagsRenderer,
  deviceTypeRenderer,
  endpointNameRenderer,
  cloudAccountRenderer,
  repositoryNameRenderer,
  registryHostRenderer,
  clusterNameRenderer,
} from './CustomGridRenderers';
import { regionRenderer } from '../../common/customRenderers';
import { DEVICES_TYPE, RiskTypes } from '../../common/constants';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { Feature } from '@nirvana/shared/constants';
import { DefaultGridColumnFixedWidths } from '@nirvana/shared/dtos/types/shared/grid';

export class DevicesAppView extends BaseWebComponentView {
  data: DevicesAppWebComponentSchema;
  public riskType = RiskTypes.DEVICES;
  public filterDependencyMapping = {
    companyId: ['endpointId', 'cveCount', 'fqdn', 'macs', 'ip', 'region', 'resourceId'],
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page_title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<ColumnIndexes, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$scanEmptyStateButton = this.shadow.querySelector('#scan-button-empty-state');
    this.dom.$scanEmptyStateTooltip = this.shadow.querySelector('#scan-button-empty-state-tooltip');
    this.dom.$overlay = this.shadow.querySelector('#extra-details-context-menu');

    this.setBulkActions();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    setTranslatedStrings.call(this);
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Resources');
  }

  async buildSuccessView(data: DevicesAppWebComponentSchema) {
    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }
      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
    });
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_devices: translateFn('All resources'),
        devices_high_severity: translateFn('High risk score'),
        devices_detected: translateFn('Detected'),
        devices_ignored: translateFn('Ignored'),
        devices_watchlist: translateFn('Watchlist'),
        devices_kubernetes: translateFn('Kubernetes'),
        devices_container_images: translateFn('Container images'),
      },
    };
  }

  private async buildSmartViews() {
    this.dependencies.componentName = 'risk-management-devices-grid';
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      this.dependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      this.dependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    this.dom.$grid.allColumnDetails = this.getGridColumns();
    this.updateFiltersBasedOnAvailability(view);
    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;
    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);
    this.dom.$grid.dataProvider = new DevicesAppGridDataProvider(this.dependencies);
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.DEVICE_NAME]: endpointNameRenderer.bind(this),
      [ColumnKeys.RISK_SCORE]: riskScoreRenderer.bind(this),
      [ColumnKeys.DEVICE_TYPE]: deviceTypeRenderer.bind(this),
      [ColumnKeys.OS]: osRenderer.bind(this),
      [ColumnKeys.REGION]: regionRenderer.bind(this),
      [ColumnKeys.DEVICE_TAGS]: deviceTagsRenderer.bind(this),
      [ColumnKeys.DEVICE_MACS]: deviceMacsRenderer.bind(this),
      [ColumnKeys.DEVICE_MISCONFIGURATIONS]: deviceMisconfigurationsRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
      [ColumnKeys.RESOURCE_ID]: resourceIdRenderer.bind(this),
      [ColumnKeys.DEVICE_IP]: ipRenderer.bind(this),
      [ColumnKeys.DEVICE_FQDN]: fqdnRenderer.bind(this),
      [ColumnKeys.CLOUD_ACCOUNT]: cloudAccountRenderer.bind(this),
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_BCST_IMAGE_SCANNER_USABLE])
        ? {
            [ColumnKeys.REPOSITORY_NAME]: repositoryNameRenderer.bind(this),
            [ColumnKeys.REGISTRY_HOST]: registryHostRenderer.bind(this),
          }
        : {}),
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_KSPM_USABLE])
        ? {
            [ColumnKeys.CLUSTER_NAME]: clusterNameRenderer.bind(this),
          }
        : {}),
    };
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.filterDependencyMapping = this.filterDependencyMapping;
    this.dom.$grid.propertiesSet = true;
    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
  }

  private getPredefinedFiltersKeys() {
    const keys: any = [];

    if (!Application.Main.company.isCustomer()) {
      keys.push(ColumnKeys.COMPANY);
    }

    return [
      ...keys,
      ColumnKeys.DEVICE_NAME,
      ColumnKeys.RISK_SCORE,
      ColumnKeys.DEVICE_CVES,
      ColumnKeys.DEVICE_MISCONFIGURATIONS,
    ];
  }

  protected getGridColumns(): Array<ColumnDetails<ColumnIndexes, ColumnKeys>> {
    const columns: any = [
      {
        column: ColumnIndexes.DEVICE_NAME,
        key: ColumnKeys.DEVICE_NAME,
        columnName: translateFn('Resource name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: '100px',
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesNames,
        },
      },
      {
        column: ColumnIndexes.RISK_SCORE,
        key: ColumnKeys.RISK_SCORE,
        columnName: translateFn('Risk score'),
        type: ColumnType.RANGE,
        width: '100px',
        removable: false,
        sortable: true,
      },
      {
        column: ColumnIndexes.DEVICE_MISCONFIGURATIONS,
        key: ColumnKeys.DEVICE_MISCONFIGURATIONS,
        columnName: translateFn('Findings'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: '80px',
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.MisconfigurationsNames,
        },
      },
      {
        column: ColumnIndexes.DEVICE_CVES,
        key: ColumnKeys.DEVICE_CVES,
        columnName: translateFn('CVEs'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesCves,
        },
      },
      {
        column: ColumnIndexes.DEVICE_TYPE,
        key: ColumnKeys.DEVICE_TYPE,
        columnName: translateFn('Resource type'),
        type: ColumnType.ENUM,
        values: this.getDeviceTypeColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: ColumnIndexes.RESOURCE_ID,
        key: ColumnKeys.RESOURCE_ID,
        columnName: translateFn('Resource ID'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesResourceId,
        },
      },
      {
        column: ColumnIndexes.OS,
        key: ColumnKeys.OS,
        columnName: translateFn('Platform'),
        type: ColumnType.ENUM,
        values: getOSTypeColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: ColumnIndexes.CLOUD_ACCOUNT,
        key: ColumnKeys.CLOUD_ACCOUNT,
        columnName: translateFn('Cloud account'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: '120px',
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesCloudAccountIds,
        },
      },
      {
        column: ColumnIndexes.REGION,
        key: ColumnKeys.REGION,
        columnName: translateFn('Region'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesRegions,
        },
      },
      {
        column: ColumnIndexes.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: getStatusColumnValues(),
        removable: false,
        sortable: false,
      },
      {
        column: ColumnIndexes.STATE,
        key: ColumnKeys.STATE,
        columnName: translateFn('State'),
        type: ColumnType.ENUM,
        values: getStateColumnValues(),
        removable: false,
        sortable: false,
      },
      {
        column: ColumnIndexes.DEVICE_FQDN,
        key: ColumnKeys.DEVICE_FQDN,
        columnName: translateFn('FQDN'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesFqdn,
        },
      },
      {
        column: ColumnIndexes.DEVICE_IP,
        key: ColumnKeys.DEVICE_IP,
        columnName: translateFn('IP'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesIp,
        },
      },
      {
        column: ColumnIndexes.DEVICE_MACS,
        key: ColumnKeys.DEVICE_MACS,
        columnName: translateFn('MAC'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.DevicesMacs,
        },
      },
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_KSPM_USABLE])
        ? [
            {
              column: ColumnIndexes.CLUSTER_NAME,
              key: ColumnKeys.CLUSTER_NAME,
              columnName: translateFn('Cluster name'),
              type: ColumnType.COMPLEX_ENUM_FILTER,
              width: '120px',
              removable: true,
              sortable: false,
              remoteDetails: {
                type: RemoteDetailsTypes.DevicesClusterNames,
              },
            },
          ]
        : []),
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_BCST_IMAGE_SCANNER_USABLE])
        ? [
            {
              column: ColumnIndexes.REPOSITORY_NAME,
              key: ColumnKeys.REPOSITORY_NAME,
              columnName: translateFn('Repository'),
              type: ColumnType.COMPLEX_ENUM_FILTER,
              width: DefaultGridColumnFixedWidths.S,
              removable: true,
              sortable: false,
              remoteDetails: {
                type: RemoteDetailsTypes.DevicesImageRepos,
              },
            },
            {
              column: ColumnIndexes.REGISTRY_HOST,
              key: ColumnKeys.REGISTRY_HOST,
              columnName: translateFn('Registry host'),
              type: ColumnType.COMPLEX_ENUM_FILTER,
              width: DefaultGridColumnFixedWidths.S,
              removable: true,
              sortable: false,
              remoteDetails: {
                type: RemoteDetailsTypes.DevicesRegistryHosts,
              },
            },
          ]
        : []),
    ];

    if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_TAGS_VISIBLE])) {
      columns.push({
        column: ColumnIndexes.DEVICE_TAGS,
        key: ColumnKeys.DEVICE_TAGS,
        columnName: translateFn('Endpoint tags'),
        type: ColumnType.TAGS,
        removable: true,
        sortable: false,
      });
    }

    columns.push({
      column: ColumnIndexes.WATCHLIST,
      key: ColumnKeys.WATCHLIST,
      columnName: this.dependencies.translateFn('In watchlist'),
      type: ColumnType.ENUM,
      values: getWatchlistColumnValues(),
      width: '100px',
      removable: true,
      sortable: false,
    });

    columns.push({
      column: ColumnIndexes.SELECT,
      key: ColumnKeys.SELECT,
      columnName: '',
      type: ColumnType.ACTION,
      removable: false,
      sortable: false,
      width: '100px',
      filteringDisabled: true,
    });

    if (Application.Main.company.isPartner()) {
      columns.unshift({
        column: ColumnIndexes.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: (Application.Main as any).user.company.id,
        },
      });
    }

    return columns;
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.DEVICE_MISCONFIGURATIONS,
        config: {
          filterLabel: this.dependencies.translateFn('Finding name'),
        },
      },
      {
        key: ColumnKeys.DEVICE_CVES,
        config: {
          filterLabel: this.dependencies.translateFn('CVE code'),
        },
      },
    ];
  }

  private getDeviceTypeColumnValues() {
    const values = Object.values(DEVICES_TYPE);

    return values.map(value => {
      return {
        label: BaseWebComponentTranslator.getInstance().getDeviceTypes(value),
        value,
      };
    });
  }

  /**
   * Filters will be "filtered" as follows:
   * - if column is not available in the grid, it will be removed from filters
   *
   * @param view
   */
  updateFiltersBasedOnAvailability(view) {
    const columnKeyMapping = {};
    for (const column of this.dom.$grid.allColumnDetails) {
      columnKeyMapping[column.key] = true;
    }

    // Keep only the filters that have a corespondent column in the grid
    view.filters = view.filters.filter(filter => {
      const columnKeys = Object.keys(filter);

      if (columnKeyMapping[columnKeys[0]]) {
        return true;
      }

      return false;
    });
  }

  private setBulkActions() {
    this.dom.fixBulk = {
      $button: this.shadow.querySelector('#fix-bulk-button'),
      $tooltip: this.shadow.querySelector('#fix-bulk-button-tooltip'),
    };
    this.dom.fixBulk.$button.innerText = translateFn('Fix risks');

    setBulkActions.call(this, {
      ignore: translateFn('Ignore resources'),
      restore: translateFn('Restore ignored resources'),
    });
  }
}
